import React, { useCallback, useEffect, useRef, useState } from "react";
import { RiCloseLine, RiZoomInLine, RiZoomOutLine, RiRestartLine } from "react-icons/ri";

export default function ImageViewer(props) {
    const ref = useRef(undefined);
    const refImage = useRef(undefined);
    const refContainer = useRef(undefined);
    const prevZoomRef = useRef(1);
    const [scaleImage, setScaling] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [lastTouchDistance, setLastTouchDistance] = useState(null);

    const MIN_ZOOM = 0.75;
    const MAX_ZOOM = 5;
    const ZOOM_STEP = 0.25;

    function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
            props.selectPicHandler(undefined);
        }
    }

    useEffect(() => {
        window.addEventListener("mousedown", handleClickOutside);
        return () => {
            window.removeEventListener("mousedown", handleClickOutside);
            if (scaleImage) setScaling(false);
        };
    }, [ref]);

    const handleKeyDown = useCallback(
        (event) => {
            if (event.key === "Escape") {
                props.selectPicHandler(undefined);
            }
        },
        [props],
    );

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleKeyDown]);

    const putErrorImage = useCallback(() => {
        refImage.current.src = "images/scrape_picture_not_available.png";
        refImage.current.className = "object-scale-down p-2 h-[95%] w-[100%] cursor-default";
    }, [refImage, props.url]);

    const handleZoomIn = useCallback(() => {
        setZoom((prev) => {
            prevZoomRef.current = prev;
            return Math.min(prev + ZOOM_STEP, MAX_ZOOM);
        });
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoom((prev) => {
            prevZoomRef.current = prev;
            return Math.max(prev - ZOOM_STEP, MIN_ZOOM);
        });
    }, []);

    // Adjust scroll position when zoom changes to maintain viewport center
    useEffect(() => {
        if (!refContainer.current || prevZoomRef.current === zoom) return;

        const container = refContainer.current;
        const oldZoom = prevZoomRef.current;
        const containerRect = container.getBoundingClientRect();

        // Calculate the center point of the visible viewport
        // When zoom is 1, there's no scroll, so use container center
        let viewportCenterX, viewportCenterY;

        if (oldZoom <= 1) {
            // Starting from zoom 1 or less, use the center of the container
            viewportCenterX = containerRect.width / 2;
            viewportCenterY = containerRect.height / 2;
        } else {
            // When zoomed in, use scroll position + viewport center
            viewportCenterX = container.scrollLeft + containerRect.width / 2;
            viewportCenterY = container.scrollTop + containerRect.height / 2;
        }

        // Calculate the zoom ratio
        const zoomRatio = zoom / oldZoom;

        // Calculate new scroll position to keep the center point in view
        const newScrollLeft = viewportCenterX * zoomRatio - containerRect.width / 2;
        const newScrollTop = viewportCenterY * zoomRatio - containerRect.height / 2;

        // Update scroll position (only if zoom > 1, otherwise container has overflow-hidden)
        if (zoom > 1) {
            container.scrollLeft = Math.max(0, newScrollLeft);
            container.scrollTop = Math.max(0, newScrollTop);
        }

        prevZoomRef.current = zoom;
    }, [zoom]);

    const handleResetZoom = useCallback(() => {
        prevZoomRef.current = zoom;
        setZoom(1);
        if (refContainer.current) {
            refContainer.current.scrollTop = 0;
            refContainer.current.scrollLeft = 0;
        }
    }, [zoom]);

    const handleWheel = useCallback(
        (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                if (!refContainer.current) return;

                const container = refContainer.current;
                const containerRect = container.getBoundingClientRect();
                const oldZoom = zoom;

                // Get mouse position relative to the container
                const mouseX = e.clientX - containerRect.left;
                const mouseY = e.clientY - containerRect.top;

                // Calculate the point in the scaled content that the mouse is over
                const contentX = (container.scrollLeft + mouseX) / oldZoom;
                const contentY = (container.scrollTop + mouseY) / oldZoom;

                const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
                const newZoom = Math.max(MIN_ZOOM, Math.min(oldZoom + delta, MAX_ZOOM));

                prevZoomRef.current = oldZoom;
                setZoom(newZoom);

                // Adjust scroll to keep the mouse point in the same position
                setTimeout(() => {
                    if (refContainer.current && newZoom > 1) {
                        const newScrollLeft = contentX * newZoom - mouseX;
                        const newScrollTop = contentY * newZoom - mouseY;
                        container.scrollLeft = Math.max(0, newScrollLeft);
                        container.scrollTop = Math.max(0, newScrollTop);
                    }
                }, 0);
            }
        },
        [zoom],
    );

    const getTouchDistance = (touch1, touch2) => {
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = useCallback((e) => {
        if (e.touches.length === 2) {
            const distance = getTouchDistance(e.touches[0], e.touches[1]);
            setLastTouchDistance(distance);
        }
    }, []);

    const handleTouchMove = useCallback(
        (e) => {
            if (e.touches.length === 2 && lastTouchDistance !== null && refContainer.current) {
                e.preventDefault();
                const container = refContainer.current;
                const containerRect = container.getBoundingClientRect();
                const oldZoom = zoom;

                // Calculate center point between the two touches
                const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - containerRect.left;
                const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - containerRect.top;

                // Calculate the point in the scaled content
                const contentX = (container.scrollLeft + centerX) / oldZoom;
                const contentY = (container.scrollTop + centerY) / oldZoom;

                const distance = getTouchDistance(e.touches[0], e.touches[1]);
                const scale = distance / lastTouchDistance;
                const newZoom = Math.max(MIN_ZOOM, Math.min(oldZoom * scale, MAX_ZOOM));

                prevZoomRef.current = oldZoom;
                setZoom(newZoom);
                setLastTouchDistance(distance);

                // Adjust scroll to keep the center point in the same position
                if (newZoom > 1) {
                    setTimeout(() => {
                        if (refContainer.current) {
                            const newScrollLeft = contentX * newZoom - centerX;
                            const newScrollTop = contentY * newZoom - centerY;
                            container.scrollLeft = Math.max(0, newScrollLeft);
                            container.scrollTop = Math.max(0, newScrollTop);
                        }
                    }, 0);
                }
            }
        },
        [lastTouchDistance, zoom],
    );

    const handleTouchEnd = useCallback(() => {
        setLastTouchDistance(null);
    }, []);

    return (
        <div ref={ref} className="fixed inset-x-[20%] inset-y-[10%] z-10 bg-[#2d3748] rounded-xl h-4/5 w-2/3 shadow-lg backdrop-blur-sm">
            <div className="relative top-1.5 right-3 flex justify-end space-x-4 mb-2">
                <div className="flex space-x-2">
                    {zoom !== 1 && (
                        <button onClick={handleResetZoom} className="p-1 text-white hover:text-secondary-500" title="Reset Zoom">
                            <RiRestartLine className="w-5 h-5" />
                        </button>
                    )}
                    <button onClick={handleZoomOut} disabled={zoom <= MIN_ZOOM} className="p-1 text-white hover:text-secondary-500 disabled:opacity-50 disabled:cursor-not-allowed" title="Zoom Out">
                        <RiZoomOutLine className="w-5 h-5" />
                    </button>
                    <button onClick={handleZoomIn} disabled={zoom >= MAX_ZOOM} className="p-1 text-white hover:text-secondary-500 disabled:opacity-50 disabled:cursor-not-allowed" title="Zoom In">
                        <RiZoomInLine className="w-5 h-5" />
                    </button>
                </div>
                <RiCloseLine className="w-5 h-5 cursor-pointer text-white hover:text-secondary-500" onClick={() => props.selectPicHandler(undefined)} />
            </div>
            <div ref={refContainer} className="h-[92%] relative overflow-auto p-4" onWheel={handleWheel} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
                <div
                    style={{
                        transform: `scale(${zoom})`,
                        transformOrigin: zoom > 1 ? "top left" : "center center",
                        transition: "transform 0.1s ease-out",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: zoom > 1 ? `${100 / zoom}%` : "100%",
                        height: "max",
                    }}
                >
                    <img
                        ref={refImage}
                        src={props.url}
                        className="object-contain select-none"
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            width: "auto",
                            height: "auto",
                            display: "block",
                            margin: "0 auto",
                        }}
                        onError={() => putErrorImage()}
                        draggable={false}
                    />
                </div>
            </div>
            {zoom > 1 && <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-2 py-1 rounded">{Math.round(zoom * 100)}%</div>}
        </div>
    );
}
